import {
  Alert,
  Box,
  Button,
  Card,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type FormEvent, useState } from "react";
import { loginTenant, type TenantUser } from "../api/tenant-auth";
import { useNotifications } from "../components/NotificationProvider";

type TenantLoginPageProps = {
  onLogin: (user: TenantUser, token: string) => void;
  onSwitchToAdmin: () => void;
};

export function TenantLoginPage({ onLogin, onSwitchToAdmin }: TenantLoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showAlert, showSnackbar } = useNotifications();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await loginTenant(trimmedEmail, password);
      onLogin(response.user, response.token);
      showSnackbar({
        title: "Signed in",
        message: `Welcome, ${response.user.firstName}!`,
        variant: "success",
      });
    } catch (err) {
      let message = "Unable to sign in";
      if (err instanceof Error) {
        try {
          const parsed = JSON.parse(err.message);
          if (parsed && parsed.error) {
            message = parsed.error;
          } else {
            message = err.message;
          }
        } catch {
          const m = err.message || "";
          if (m.includes("Failed to fetch") || m.includes("NetworkError")) {
            message = "Cannot reach server";
          } else {
            message = m;
          }
        }
      }

      setError(message);
      showAlert({ title: "Sign-in failed", message, variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f4f7fb",
        p: 2,
      }}
    >
      <Card
        sx={{
          width: "min(100%, 480px)",
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.12)",
          border: "1px solid rgba(15, 23, 42, 0.06)",
        }}
      >
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            background: "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)",
            color: "white",
          }}
        >
          <Typography
            variant="overline"
            sx={{ letterSpacing: 1.4, opacity: 0.9 }}
          >
            Tenant Portal
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
            Welcome
          </Typography>
          <Typography sx={{ mt: 1, color: "rgba(255,255,255,0.82)" }}>
            Sign in to view your tenancy details and upload benefit documents.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={2.5}>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="your@email.com"
                  required
                  fullWidth
                  autoComplete="email"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter your password"
                  required
                  fullWidth
                  autoComplete="current-password"
                />
                {error ? <Alert severity="error">{error}</Alert> : null}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    py: 1.3,
                    borderRadius: 2,
                    fontWeight: 600,
                    boxShadow: "none",
                    bgcolor: "teal.main",
                    "&:hover": { boxShadow: "none", bgcolor: "teal.dark" },
                  }}
                >
                  {isSubmitting ? "Signing in..." : "Sign in"}
                </Button>
              </Stack>
            </Box>

            <Button
              variant="text"
              onClick={onSwitchToAdmin}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Sign in as administrator instead
            </Button>
          </Stack>
        </Box>
      </Card>
    </Box>
  );
}
